import { BankingTabs } from '@/components/banking/BankingTabs';
import { OverviewTabs, type OverviewTab } from '@/components/dashboard/OverviewTabs';
import { PillarHeroAvatar } from '@/components/layout/PillarHeroAvatar';
import { PlanningTabs } from '@/components/planning/PlanningTabs';
import { SuccessionTabLoadingShell } from '@/components/planning/succession/SuccessionTabLoadingShell';
import { TradeTabNav } from '@/components/trading/TradeTabNav';
import { PageHero } from '@/components/ui/page-hero';
import {
  PillarMetricSubtitleRow,
  PillarMetricWithInfo,
} from '@/components/ui/pillar-metric-with-info';
import { Skeleton } from '@/components/ui/skeleton';
import { useBankingHeroMetrics } from '@/features/accounts/useBankingHeroMetrics';
import { PLANNING_TABS, type PlanningTab } from '@/features/planning';
import { parseTradeTab } from '@/features/trading/types';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { CandlestickChart, LayoutGrid, TrendingUp, Wallet, type LucideIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

type Pillar = 'overview' | 'banking' | 'trade' | 'planning';

export const DEFAULT_POSITIONS_MOCK_ROWS: string[][] = [
  ['AAPL', 'Brokerage', '120', '€ 24,380.00', '+€ 1,240.00'],
  ['MSFT', 'ISA', '45', '€ 18,920.00', '−€ 320.00'],
  ['VWCE', 'Pension', '200', '€ 32,100.00', '+€ 890.00'],
  ['BTC', 'Crypto', '0.5', '€ 21,400.00', '−€ 1,100.00'],
  ['GOOG', 'Brokerage', '30', '€ 5,640.00', '+€ 210.00'],
  ['AMZN', 'Brokerage', '15', '€ 2,890.00', '+€ 45.00'],
];

const DEFAULT_ORDERS_MOCK_ROWS: string[][] = [
  ['AAPL', 'Brokerage', 'buy', 'market', '10', 'filled'],
  ['MSFT', 'ISA', 'sell', 'limit', '5', 'pending'],
  ['VWCE', 'Pension', 'buy', 'market', '20', 'filled'],
  ['BTC', 'Crypto', 'buy', 'market', '0.1', 'cancelled'],
  ['GOOG', 'Brokerage', 'sell', 'limit', '8', 'filled'],
  ['AMZN', 'Brokerage', 'buy', 'market', '3', 'rejected'],
];

const DEFAULT_GOALS_MOCK_ROWS: string[][] = [
  ['Emergency fund', 'On track', '68%', '€ 25,000', 'Mandatory'],
  ['House deposit', 'At risk', '42%', '€ 80,000', 'Mandatory'],
  ['Retirement', 'On track', '31%', '€ 500,000', 'Nice to have'],
  ['New car', 'Behind', '12%', '€ 15,000', 'Nice to have'],
  ['Holiday', 'On track', '85%', '€ 4,000', 'Nice to have'],
  ['Education', 'At risk', '55%', '€ 30,000', 'Mandatory'],
];

function resolvePillar(pathname: string): Pillar | null {
  if (pathname === '/' || pathname.startsWith('/overview')) return 'overview';
  if (
    pathname.startsWith('/banking') ||
    pathname.startsWith('/accounts') ||
    pathname === '/accounts'
  ) {
    return 'banking';
  }
  if (pathname.startsWith('/trade')) return 'trade';
  if (pathname.startsWith('/planning')) return 'planning';
  return null;
}

function parseOverviewTab(pathname: string): OverviewTab {
  if (pathname.includes('/net-worth')) return 'net-worth';
  if (pathname.includes('/financial-plan')) return 'financial-plan';
  return 'summary';
}

type BankingTab = 'accounts' | 'transactions';

function parseBankingTab(pathname: string): BankingTab {
  if (pathname.includes('/transactions')) return 'transactions';
  return 'accounts';
}

function parsePlanningRoute(pathname: string): { tab: PlanningTab; planId?: string } {
  if (pathname === '/planning' || pathname === '/planning/') {
    return { tab: 'overview' };
  }
  if (pathname.startsWith('/planning/goals')) {
    return { tab: 'goals' };
  }
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'planning' || segments.length < 2) {
    return { tab: 'overview' };
  }
  const planId = segments[1];
  const maybeTab = segments[2] as PlanningTab | undefined;
  if (maybeTab && (PLANNING_TABS as readonly string[]).includes(maybeTab)) {
    return { tab: maybeTab, planId };
  }
  return { tab: 'overview', planId };
}

function parseTradeTabFromPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  const tabIndex = segments.indexOf('trade') + 1;
  return parseTradeTab(segments[tabIndex]);
}

interface HeroMetricConfig {
  icon: LucideIcon;
  titleInfo: string;
  titleInfoAria: string;
  subtitleLabel: string;
  subtitleInfo: string;
  subtitleInfoAria: string;
  titleValue?: number;
  subtitleValue?: number;
  isLoading: boolean;
}

function buildHeroNode(config: HeroMetricConfig, currency: string): ReactNode {
  const format = (n: number) => formatCurrency(n, currency);
  return (
    <PageHero
      avatar={<PillarHeroAvatar icon={config.icon} />}
      title={
        <PillarMetricWithInfo
          value={config.titleValue != null ? format(config.titleValue) : ''}
          numericValue={config.titleValue}
          formatValue={format}
          info={config.titleInfo}
          infoAriaLabel={config.titleInfoAria}
          isLoading={config.isLoading}
        />
      }
      subtitle={
        <PillarMetricSubtitleRow
          label={config.subtitleLabel}
          value={config.subtitleValue != null ? format(config.subtitleValue) : ''}
          numericValue={config.subtitleValue}
          formatValue={format}
          info={config.subtitleInfo}
          infoAriaLabel={config.subtitleInfoAria}
          isLoading={config.isLoading}
        />
      }
    />
  );
}

export function usePageSkeletonPreset() {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const currency = useDefaultCurrency();
  const bankingMetrics = useBankingHeroMetrics();
  const pillar = resolvePillar(pathname);

  return useMemo(() => {
    if (!pillar) {
      return {
        hero: null,
        tabs: null,
        tableHeaders: [] as string[],
        tableRows: [] as string[][],
        content: null,
      };
    }

    let tabs: ReactNode = null;
    let tableHeaders: string[] = [];
    let tableRows: string[][] = DEFAULT_POSITIONS_MOCK_ROWS;
    let hero: ReactNode = null;
    let content: ReactNode = null;

    if (pillar === 'overview') {
      const active = parseOverviewTab(pathname);
      tabs = <OverviewTabs active={active} />;
      tableHeaders = [
        t('trade.positions.symbol'),
        t('trade.positions.account'),
        t('trade.positions.quantity'),
        t('trade.positions.marketValue'),
        t('trade.positions.unrealizedPnL'),
      ];
      hero = buildHeroNode(
        {
          icon: LayoutGrid,
          titleInfo: t('overview.hero.netWorthInfo'),
          titleInfoAria: t('overview.hero.netWorthInfoAria'),
          subtitleLabel: t('overview.hero.liquidAssets'),
          subtitleInfo: t('banking.hero.checkingBalanceInfo'),
          subtitleInfoAria: t('banking.hero.checkingBalanceInfoAria'),
          isLoading: true,
        },
        currency
      );
    } else if (pillar === 'banking') {
      const active = parseBankingTab(pathname);
      tabs = <BankingTabs active={active} />;
      tableHeaders = [
        t('trade.positions.symbol'),
        t('trade.positions.account'),
        t('trade.positions.quantity'),
        t('trade.positions.marketValue'),
        t('trade.positions.unrealizedPnL'),
      ];
      const { checkingBalance, monthlySalary, isLoading } = bankingMetrics;
      hero = buildHeroNode(
        {
          icon: Wallet,
          titleInfo: t('banking.hero.checkingBalanceInfo'),
          titleInfoAria: t('banking.hero.checkingBalanceInfoAria'),
          subtitleLabel: t('banking.hero.monthlySalary'),
          subtitleInfo: t('banking.hero.monthlySalaryInfo'),
          subtitleInfoAria: t('banking.hero.monthlySalaryInfoAria'),
          titleValue: isLoading ? undefined : checkingBalance,
          subtitleValue: isLoading ? undefined : monthlySalary,
          isLoading,
        },
        currency
      );
    } else if (pillar === 'trade') {
      const activeTab = parseTradeTabFromPath(pathname);
      tabs = <TradeTabNav activeTab={activeTab} />;
      if (activeTab === 'orders') {
        tableHeaders = [
          t('trade.orders.asset'),
          t('trade.orders.account'),
          t('trade.orders.side'),
          t('trade.orders.type'),
          t('trade.orders.quantity'),
          t('trade.orders.status'),
        ];
        tableRows = DEFAULT_ORDERS_MOCK_ROWS;
      } else {
        tableHeaders = [
          t('trade.positions.symbol'),
          t('trade.positions.account'),
          t('trade.positions.quantity'),
          t('trade.positions.marketValue'),
          t('trade.positions.unrealizedPnL'),
        ];
        tableRows = DEFAULT_POSITIONS_MOCK_ROWS;
      }
      hero = buildHeroNode(
        {
          icon: CandlestickChart,
          titleInfo: t('trade.hero.investmentsInfo'),
          titleInfoAria: t('trade.hero.investmentsInfoAria'),
          subtitleLabel: t('trade.hero.unrealizedPnL'),
          subtitleInfo: t('trade.hero.unrealizedPnLInfo'),
          subtitleInfoAria: t('trade.hero.unrealizedPnLInfoAria'),
          isLoading: true,
        },
        currency
      );
    } else if (pillar === 'planning') {
      const { tab, planId } = parsePlanningRoute(pathname);
      tabs = <PlanningTabs activeTab={tab} planId={planId} />;
      if (tab === 'goals') {
        tableHeaders = [
          t('goals.table.goal'),
          t('goals.table.status'),
          t('goals.table.progress'),
          t('goals.table.target'),
          t('goals.table.priority'),
        ];
        tableRows = DEFAULT_GOALS_MOCK_ROWS;
      } else if (tab === 'succession') {
        content = <SuccessionTabLoadingShell />;
      } else {
        content = <Skeleton className='h-[480px] w-full rounded-xl' aria-hidden />;
      }
      hero = buildHeroNode(
        {
          icon: TrendingUp,
          titleInfo: t('planning.hero.netWorthInfo'),
          titleInfoAria: t('planning.hero.netWorthInfoAria'),
          subtitleLabel: t('planning.hero.monthlySaving'),
          subtitleInfo: t('planning.hero.monthlySavingInfo'),
          subtitleInfoAria: t('planning.hero.monthlySavingInfoAria'),
          isLoading: true,
        },
        currency
      );
    }

    return { hero, tabs, tableHeaders, tableRows, content };
  }, [bankingMetrics, currency, pathname, pillar, t]);
}
